import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../api';
import { useWS } from '../ws';
import BarcodeScanner from '../components/BarcodeScanner';
import { useConfirm } from '../components/ConfirmDialog';
import { isNative, isIOS, isAndroid, scanTextOCR } from '../native';
import { useTranslation } from 'react-i18next';

const UNITS = ['stk','g','kg','ml','l','pk','bk','pose','bakke','flaske','dåse'];
const SHOPPING_COLOR = 'oklch(60% 0.18 290)';
const FONT = 'Manrope, sans-serif';

export default function ShoppingPage() {
  const { t } = useTranslation();
  const [items, setItems]       = useState([]);
  const [catalog, setCatalog]   = useState([]);
  const [search, setSearch]     = useState('');
  const [showAdd, setShowAdd]   = useState(false);
  const [scanning, setScanning] = useState(false);
  const [newItem, setNewItem]       = useState({ text: '', qty: 1, unit: 'stk' });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [addError, setAddError]     = useState('');
  const [editingId, setEditingId]   = useState(null);
  const [editQty, setEditQty]   = useState(1);
  const [editUnit, setEditUnit] = useState('stk');
  const [ocrLines, setOcrLines]   = useState(null); // null = lukket, [...] = preview åben
  const [ocrSaving, setOcrSaving] = useState(false);
  const [ocrError, setOcrError]   = useState('');
  const [ocrLoading, setOcrLoading] = useState(false);
  const [kbHeight, setKbHeight]   = useState(0);
  const { user } = useAuth();
  const userName = user?.display_name || user?.email?.split('@')[0] || 'Anonym';
  const { confirm, ConfirmDialog } = useConfirm();

  const load = useCallback(async () => {
    const [list, cat] = await Promise.all([api.getShopping(), api.getCatalog()]);
    setItems(list); setCatalog(cat);
  }, []);

  useEffect(() => { load(); }, [load]);
  useWS(useCallback((msg) => { if (msg.event === 'shopping:changed') load(); }, [load]));

  // Keyboard-højde på iOS — fixed sheets forbliver bag keyboard uden denne justering
  useEffect(() => {
    if (!isIOS()) return;
    let l1, l2;
    import(/* @vite-ignore */ '@capacitor/keyboard').then(({ Keyboard }) => {
      Keyboard.addListener('keyboardWillShow', info => setKbHeight(info.keyboardHeight)).then(l => { l1 = l; });
      Keyboard.addListener('keyboardWillHide', () => setKbHeight(0)).then(l => { l2 = l; });
    }).catch(() => {});
    return () => { l1?.remove(); l2?.remove(); };
  }, []);

  const toggle = async (item) => {
    await api.updateShopping(item.id, { checked: !item.checked, checked_by: userName });
    load();
  };

  const prefillFromCatalog = (catalogItem) => {
    setSelectedProduct(catalogItem);
    setNewItem({ text: catalogItem.name, qty: 1, unit: catalogItem.unit || 'stk' });
    setAddError('');
  };

  const addCustom = async () => {
    if (!newItem.text.trim()) return;
    setAddError('');
    try {
      const payload = selectedProduct
        ? { product_id: selectedProduct.id, quantity: newItem.qty, unit: newItem.unit, added_by: userName }
        : { custom_name: newItem.text, quantity: newItem.qty, unit: newItem.unit, added_by: userName };
      await api.addShopping(payload);
      setNewItem({ text: '', qty: 1, unit: 'stk' });
      setSelectedProduct(null);
      setSearch(''); setShowAdd(false);
      load();
    } catch (err) {
      setAddError(err.message || t('shopping.addError'));
    }
  };

  const handleBarcode = async (code) => {
    setScanning(false);
    try {
      const { source, product } = await api.lookupBarcode(code);
      if (source === 'local' && product) await addFromSearch(product);
      else { setNewItem(n => ({ ...n, text: product?.name || code })); setShowAdd(true); }
    } catch {
      setNewItem(n => ({ ...n, text: code }));
      setShowAdd(true);
    }
  };

  const parseOcrLine = (raw) => {
    const unitPattern = 'kg|g|liter|l|dl|ml|cl|stk|pk|pkt|pakke|dåse|pose|flaske|boks|bundt|bakke';
    // "500g hakket oksekød" eller "1,5 l mælk"
    const m = raw.match(new RegExp(`^(\\d+(?:[.,]\\d+)?)\\s*(${unitPattern})\\.?\\s*(.+)$`, 'i'));
    if (m) return { quantity: parseFloat(m[1].replace(',', '.')), unit: m[2].toLowerCase(), name: m[3].trim() };
    // "3 æg" (tal uden enhed)
    const m2 = raw.match(/^(\d+(?:[.,]\d+)?)\s+(.+)$/);
    if (m2) return { quantity: parseFloat(m2[1].replace(',', '.')), unit: 'stk', name: m2[2].trim() };
    // ingen tal — default
    return { quantity: 1, unit: 'stk', name: raw.trim() };
  };

  const handleOCR = async () => {
    setOcrError('');
    setOcrLoading(true);
    try {
      const text = await scanTextOCR();
      if (!text) { setOcrError(t('shopping.ocrNoText')); return; }
      const lines = text
        .split('\n')
        .map(l => l.trim())
        .filter(l => l.length >= 2)
        .map((t, i) => {
          const { quantity, unit, name } = parseOcrLine(t);
          return { id: i, text: name.charAt(0).toUpperCase() + name.slice(1), quantity, unit, error: null };
        });
      if (lines.length === 0) { setOcrError(t('shopping.ocrNoLines')); return; }
      setOcrLines(lines);
    } catch (err) {
      setOcrError(`Scanning fejlede: ${err.message || 'ukendt fejl'}`);
    } finally {
      setOcrLoading(false);
    }
  };

  const saveOcrLines = async () => {
    setOcrSaving(true);
    const results = await Promise.allSettled(
      ocrLines.map(line => api.addShopping({ custom_name: line.text, quantity: line.quantity, unit: line.unit, added_by: userName }))
    );
    const withErrors = ocrLines
      .map((line, i) => ({ ...line, error: results[i].status === 'rejected' ? (results[i].reason?.message || 'Fejl') : null }))
      .filter(line => line.error);
    setOcrSaving(false);
    load();
    if (withErrors.length === 0) setOcrLines(null);
    else setOcrLines(withErrors);
  };

  const deleteItem = async (id) => { await api.deleteShopping(id); load(); };

  const clearChecked = async () => {
    const ok = await confirm('Ryd alle afkrydsede varer?');
    if (!ok) return;
    await api.clearChecked();
    load();
  };

  const startEdit = (item) => { setEditingId(item.id); setEditQty(item.quantity); setEditUnit(item.unit || 'stk'); };
  const saveEdit  = async (item) => { await api.updateShopping(item.id, { quantity: editQty, unit: editUnit }); setEditingId(null); load(); };

  const unchecked  = items.filter(i => !i.checked);
  const checked    = items.filter(i => i.checked);
  const autoAdded  = unchecked.filter(i => i.auto_added);
  const regular    = unchecked.filter(i => !i.auto_added);

  const filteredCatalog = catalog.filter(p =>
    (p.name.toLowerCase().includes(search.toLowerCase()) || (p.brand || '').toLowerCase().includes(search.toLowerCase())) &&
    !items.some(i => i.product_id === p.id && !i.checked)
  );

  return (
    <>
      {ConfirmDialog}
      {scanning && <BarcodeScanner onScan={handleBarcode} onClose={() => setScanning(false)} />}

      <div className="flex flex-col h-screen" style={{ background: 'var(--bg-base)' }}>

        {/* Header */}
        <div className="pt-safe flex-shrink-0" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
          {ocrError && (
            <div style={{ margin: '8px 16px 0', padding: '10px 14px', background: 'oklch(40% 0.15 25 / 12%)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <p style={{ fontSize: 13, color: 'var(--status-expired)', fontFamily: FONT, margin: 0 }}>{ocrError}</p>
              <button type="button" onClick={() => setOcrError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--fg-3)', fontSize: 16, lineHeight: 1, padding: 0, flexShrink: 0 }}>×</button>
            </div>
          )}
          <div style={{ padding: '14px 16px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <h1 className="ft-screen-title">{t('shopping.title')}</h1>
              <p className="ft-screen-subtitle">
                {unchecked.length > 0 ? t('shopping.itemsMissing', { count: unchecked.length }) : t('shopping.allFound')} · {userName}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {isNative() && (
                <button type="button"
                  onPointerDown={e => { e.preventDefault(); if (!ocrLoading) handleOCR(); }}
                  style={{ width: 44, height: 44, borderRadius: '50%', border: 'none', background: 'var(--bg-elevated)', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: ocrLoading ? 0.5 : 1, touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}>
                  {ocrLoading ? '⏳' : '📄'}
                </button>
              )}
              <button type="button" onClick={() => setScanning(true)}
                style={{ width: 40, height: 40, borderRadius: '50%', border: 'none', background: 'var(--bg-elevated)', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                📷
              </button>
              <button type="button" onClick={() => setShowAdd(true)}
                style={{ width: 46, height: 46, borderRadius: '50%', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 300, color: '#fff', background: SHOPPING_COLOR, WebkitTapHighlightColor: 'transparent' }}>
                +
              </button>
            </div>
          </div>
        </div>

        {/* Liste */}
        <div className="flex-1 overflow-y-auto pb-safe">
          {items.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 64, gap: 8 }}>
              <span style={{ fontSize: 48 }}>🛒</span>
              <p style={{ fontSize: 15, color: 'var(--fg-3)', fontFamily: FONT }}>{t('shopping.empty')}</p>
              <button type="button" onClick={() => setShowAdd(true)}
                style={{ color: SHOPPING_COLOR, fontSize: 14, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                {t('shopping.addItems')}
              </button>
            </div>
          ) : (
            <>
              {/* ── Auto-tilføjet boble ─────────────────────────────────── */}
              {autoAdded.length > 0 && (
                <div style={{
                  margin: '12px 14px 4px',
                  background: 'oklch(80% 0.12 175 / 10%)',
                  border: '1px solid oklch(80% 0.12 175 / 28%)',
                  borderRadius: 16,
                  overflow: 'hidden',
                }}>
                  <div style={{ padding: '10px 14px 8px', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid oklch(80% 0.12 175 / 20%)' }}>
                    <span style={{ fontSize: 15 }}>🤖</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--mint-300)', fontFamily: FONT, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                      {t('shopping.autoAdded')}
                    </span>
                  </div>
                  {autoAdded.map(item => (
                    <ShoppingItem key={item.id} item={item}
                      editing={editingId === item.id} editQty={editQty} editUnit={editUnit}
                      onEditQty={setEditQty} onEditUnit={setEditUnit}
                      onToggle={() => toggle(item)} onDelete={() => deleteItem(item.id)}
                      onStartEdit={() => startEdit(item)} onSaveEdit={() => saveEdit(item)}
                      onCancelEdit={() => setEditingId(null)}
                      accentColor="var(--mint-300)" />
                  ))}
                </div>
              )}

              {/* ── Ikke-fundne varer ────────────────────────────────────── */}
              {regular.length > 0 && (
                <div style={{ background: 'var(--bg-card)', marginTop: autoAdded.length > 0 ? 4 : 0 }}>
                  {regular.map(item => (
                    <ShoppingItem key={item.id} item={item}
                      editing={editingId === item.id} editQty={editQty} editUnit={editUnit}
                      onEditQty={setEditQty} onEditUnit={setEditUnit}
                      onToggle={() => toggle(item)} onDelete={() => deleteItem(item.id)}
                      onStartEdit={() => startEdit(item)} onSaveEdit={() => saveEdit(item)}
                      onCancelEdit={() => setEditingId(null)}
                      accentColor={SHOPPING_COLOR} />
                  ))}
                </div>
              )}

              {/* ── Fundne varer ─────────────────────────────────────────── */}
              {checked.length > 0 && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px 5px', background: 'var(--bg-sunken)' }}>
                    <span className="ft-section-label">{t('shopping.found', { count: checked.length })}</span>
                    <button type="button" onClick={clearChecked}
                      style={{ fontSize: 12, color: 'var(--status-expired)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT }}>
                      {t('shopping.clearFound')}
                    </button>
                  </div>
                  <div style={{ background: 'var(--bg-card)', opacity: 0.7 }}>
                    {checked.map(item => (
                      <ShoppingItem key={item.id} item={item}
                        editing={false} editQty={1} editUnit="stk"
                        onEditQty={() => {}} onEditUnit={() => {}}
                        onToggle={() => toggle(item)} onDelete={() => deleteItem(item.id)}
                        onStartEdit={() => {}} onSaveEdit={() => {}} onCancelEdit={() => {}}
                        accentColor={SHOPPING_COLOR} />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* OCR preview-sheet */}
        {ocrLines && (
          <div className="fixed inset-0 z-40" style={{ background: 'var(--bg-overlay)' }}
            onClick={() => !ocrSaving && setOcrLines(null)}>
            <div className="fixed bottom-0 left-0 right-0 ft-sheet"
              style={{ maxHeight: kbHeight ? `calc(100vh - ${kbHeight}px - 60px)` : '80vh', overflowY: 'auto', paddingBottom: 'calc(5rem + env(safe-area-inset-bottom))', marginBottom: kbHeight || 0 }}
              onClick={e => e.stopPropagation()}>
              <div className="ft-sheet-handle" />

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px 12px', borderBottom: '1px solid var(--border-subtle)' }}>
                <h3 style={{ fontSize: 17, fontWeight: 700, color: 'var(--fg-1)', fontFamily: FONT }}>{t('shopping.ocrScanned')}</h3>
                <button type="button" onClick={() => !ocrSaving && setOcrLines(null)}
                  style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', background: 'var(--bg-elevated)', fontSize: 18, cursor: 'pointer', color: 'var(--fg-3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>

              <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <p style={{ fontSize: 13, color: 'var(--fg-3)', fontFamily: FONT }}>{t('shopping.editLines')}</p>

                {ocrLines.map((line, i) => (
                  <div key={line.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input
                          value={line.text}
                          onChange={e => setOcrLines(prev => prev.map((l, j) => j === i ? { ...l, text: e.target.value, error: null } : l))}
                          className="input-field"
                          style={{ flex: 1, minWidth: 0, borderColor: line.error ? 'var(--status-expired)' : undefined }}
                        />
                        <input
                          value={line.quantity}
                          onChange={e => setOcrLines(prev => prev.map((l, j) => j === i ? { ...l, quantity: e.target.value } : l))}
                          className="input-field"
                          style={{ width: 52, textAlign: 'center', flexShrink: 0, padding: '8px 4px' }}
                        />
                        <input
                          value={line.unit}
                          onChange={e => setOcrLines(prev => prev.map((l, j) => j === i ? { ...l, unit: e.target.value } : l))}
                          className="input-field"
                          style={{ width: 58, flexShrink: 0, padding: '8px 4px' }}
                        />
                      </div>
                      {line.error && <p style={{ fontSize: 11, color: 'var(--status-expired)', marginTop: 3 }}>{line.error}</p>}
                    </div>
                    <button type="button"
                      onClick={() => setOcrLines(prev => prev.filter((_, j) => j !== i))}
                      style={{ width: 36, height: 36, borderRadius: '50%', border: 'none', background: 'var(--bg-elevated)', fontSize: 18, color: 'var(--fg-3)', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                  </div>
                ))}

                <button type="button" onClick={saveOcrLines}
                  disabled={ocrSaving || ocrLines.length === 0}
                  style={{ marginTop: 8, padding: '14px', borderRadius: 14, border: 'none', background: SHOPPING_COLOR, color: 'white', fontWeight: 700, fontSize: 16, cursor: 'pointer', fontFamily: FONT, opacity: (ocrSaving || ocrLines.length === 0) ? 0.5 : 1 }}>
                  {ocrSaving ? t('shopping.ocrAdding') : t('shopping.ocrAdd', { count: ocrLines.length })}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tilføj-sheet */}
        {showAdd && (
          <div className="fixed inset-0 z-40" style={{ background: 'var(--bg-overlay)' }}
            onClick={() => { setShowAdd(false); setSelectedProduct(null); setNewItem({ text: '', qty: 1, unit: 'stk' }); setAddError(''); }}>
            <div className="fixed bottom-0 left-0 right-0 ft-sheet"
              style={{ maxHeight: '80vh', overflowY: 'auto', paddingBottom: 'calc(5rem + env(safe-area-inset-bottom))', marginBottom: kbHeight || 0 }}
              onClick={e => e.stopPropagation()}>
              <div className="ft-sheet-handle" />

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px 12px', borderBottom: '1px solid var(--border-subtle)' }}>
                <h3 style={{ fontSize: 17, fontWeight: 700, color: 'var(--fg-1)', fontFamily: FONT }}>{t('shopping.addToList')}</h3>
                <button type="button" onClick={() => { setShowAdd(false); setSelectedProduct(null); setNewItem({ text: '', qty: 1, unit: 'stk' }); setAddError(''); }}
                  style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', background: 'var(--bg-elevated)', fontSize: 18, cursor: 'pointer', color: 'var(--fg-3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>

              <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div className="ft-search">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--fg-4)', flexShrink: 0 }}>
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <input value={search} onChange={e => setSearch(e.target.value)}
                    placeholder={t('shopping.searchCatalog')} autoFocus={!isIOS()} />
                </div>

                <div style={{ background: 'var(--bg-elevated)', borderRadius: 14, padding: '12px 14px' }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-4)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: FONT, marginBottom: 8 }}>{t('shopping.addManually')}</p>
                  <input value={newItem.text} onChange={e => { setNewItem(n => ({ ...n, text: e.target.value })); setSelectedProduct(null); }}
                    onKeyDown={e => e.key === 'Enter' && addCustom()}
                    className="input-field" style={{ marginBottom: 8 }} placeholder={t('shopping.itemName')} />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input type="number" min="1" value={newItem.qty}
                      onChange={e => setNewItem(n => ({ ...n, qty: parseInt(e.target.value)||1 }))}
                      className="input-field" style={{ width: 72 }} />
                    <select value={newItem.unit} onChange={e => setNewItem(n => ({ ...n, unit: e.target.value }))}
                      className="input-field" style={{ flex: 1 }}>
                      {UNITS.map(u => <option key={u}>{u}</option>)}
                    </select>
                    <button type="button" onClick={addCustom} disabled={!newItem.text.trim()}
                      style={{ padding: '0 16px', borderRadius: 10, border: 'none', background: SHOPPING_COLOR, color: 'white', fontWeight: 600, fontSize: 18, cursor: 'pointer', opacity: !newItem.text.trim() ? 0.4 : 1, flexShrink: 0 }}>+</button>
                  </div>
                  {addError && <p style={{ fontSize: 12, color: 'var(--status-expired)', marginTop: 6 }}>{addError}</p>}
                </div>

                {filteredCatalog.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-4)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: FONT, marginBottom: 6 }}>{t('shopping.fromCatalog')}</p>
                    {filteredCatalog.slice(0, 20).map(p => {
                      const isSelected = selectedProduct?.id === p.id;
                      return (
                        <button key={p.id} type="button" onClick={() => prefillFromCatalog(p)}
                          className="ft-list-item" style={{ width: '100%', textAlign: 'left', background: isSelected ? `oklch(60% 0.18 290 / 10%)` : undefined, borderRadius: isSelected ? 10 : undefined }}>
                          <div className="ft-thumb">
                            {p.thumbnail
                              ? <img src={p.thumbnail} alt="" className="w-full h-full object-cover" />
                              : <span style={{ fontSize: 22 }}>📦</span>}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="ft-item-name">{p.name}</p>
                            {p.brand && <p className="ft-item-meta">{p.brand}</p>}
                          </div>
                          <span style={{ color: isSelected ? SHOPPING_COLOR : 'var(--fg-4)', fontSize: isSelected ? 22 : 18, fontWeight: 700, transition: 'all 150ms' }}>{isSelected ? '✓' : '+'}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function ShoppingItem({ item, editing, editQty, editUnit, onEditQty, onEditUnit, onToggle, onDelete, onStartEdit, onSaveEdit, onCancelEdit, accentColor }) {
  const { t } = useTranslation();
  const name = item.product_name || item.custom_name || 'Ukendt';

  if (editing) return (
    <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-elevated)' }}>
      <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--fg-1)', fontFamily: FONT, marginBottom: 8 }}>{name}</p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input type="number" min="1" value={editQty} onChange={e => onEditQty(parseInt(e.target.value)||1)}
          className="input-field" style={{ width: 72, textAlign: 'center' }} autoFocus />
        <select value={editUnit} onChange={e => onEditUnit(e.target.value)}
          className="input-field" style={{ flex: 1 }}>
          {UNITS.map(u => <option key={u}>{u}</option>)}
        </select>
        <button type="button" onClick={onCancelEdit}
          style={{ padding: '10px 14px', borderRadius: 10, border: 'none', background: 'var(--bg-sunken)', color: 'var(--fg-2)', cursor: 'pointer' }}>✕</button>
        <button type="button" onClick={onSaveEdit}
          style={{ padding: '10px 14px', borderRadius: 10, border: 'none', background: 'var(--brand)', color: 'white', fontWeight: 600, cursor: 'pointer' }}>✓</button>
      </div>
    </div>
  );

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
      borderBottom: '1px solid var(--border-subtle)',
      opacity: item.checked ? 0.6 : 1,
    }}>
      <button type="button" onClick={e => { e.stopPropagation(); onToggle(); }}
        style={{
          width: 28, height: 28, borderRadius: '50%', flexShrink: 0, cursor: 'pointer',
          border: item.checked ? 'none' : '2px solid var(--border-default)',
          background: item.checked ? 'var(--status-fresh)' : 'transparent',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 150ms',
        }}>
        {item.checked && <span style={{ color: 'white', fontSize: 14, fontWeight: 700 }}>✓</span>}
      </button>

      <div style={{ flex: 1, minWidth: 0 }} onClick={!item.checked ? onStartEdit : undefined}>
        <p style={{
          fontSize: 15, fontWeight: 600, fontFamily: FONT,
          color: 'var(--fg-1)', textDecoration: item.checked ? 'line-through' : 'none',
        }}>{name}</p>
        <div style={{ display: 'flex', gap: 6, fontSize: 12, color: 'var(--fg-3)', fontFamily: FONT, alignItems: 'center' }}>
          <span style={{ color: item.checked ? 'var(--fg-4)' : accentColor, fontWeight: 600 }}>
            {(() => {
              const weightUnits = ['g','kg','ml','l','cl','dl'];
              const pu = item.product_unit;
              const wpv = parseFloat(item.product_weight_per_unit);
              if (item.unit === 'stk' && wpv && weightUnits.includes(pu)) {
                const total = item.quantity * wpv;
                if ((pu === 'g' || pu === 'ml') && total >= 1000)
                  return `${item.quantity} stk · ${parseFloat((total/1000).toFixed(2))} ${pu === 'g' ? 'kg' : 'l'}`;
                return `${item.quantity} stk · ${total} ${pu}`;
              }
              return `${item.quantity} ${item.unit || 'stk'}`;
            })()}
          </span>
          {item.added_by && <span>· {item.added_by}</span>}
          {item.checked && item.checked_by && (
            <span style={{ color: 'var(--status-fresh)' }}>· {t('shopping.foundBy', { name: item.checked_by })}</span>
          )}
        </div>
      </div>

      {!item.checked && (
        <button type="button" onClick={onStartEdit}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--fg-4)', fontSize: 16, padding: '4px' }}>✏️</button>
      )}
      <button type="button" onClick={onDelete}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--fg-4)', fontSize: 20, padding: '4px', lineHeight: 1 }}>×</button>
    </div>
  );
}
